import { refreshTimeEntryCaches } from '@/features/grid/utils/refreshTimeEntryCaches'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import NewTimeEntryDialog from '@features/timeEntries/components/dialogs/NewTimeEntryDialog'
import { ZendeskWidget } from '../../matteroomTab/ZendeskWidget'
import HeartBeatDialog from '../ContentPage/web/HeartBeatDialog'
import MidnightClockingDialog from '../../matteroomTab/MidnightClockingDialog'
import { useWorkspaceActions } from '../../features/appShell/components/WorkspaceActionsContext'
import { useMidnightClocking } from '../../features/timeEntries/hooks/useMidnightClocking'
import { useConnectedAppsStore } from '../../stores/useConnectedAppsStore'
import { useDeviceStore } from '../../stores/useDeviceStore'
import { useTimeEntriesStore } from '../../stores/useTimeEntriesStore'
import { useUserStore } from '../../stores/useUserStore'
import { useWorkspaceUiStore } from '../../stores/useWorkspaceUiStore'
import { ROUTER_PATH } from '../../features/router/config/constants'
import WelcomeWizard from '@/features/onboarding/components/WelcomeWizard'
import { queryClient } from '@/lib/queryClient'
import { TimeEntryDeleteConfirmationDialog } from '@/components/common/dialog/TimeEntryDeleteConfirmationDialog'
import { GlobalTitleTooltip } from '@/components/common/GlobalTitleTooltip'

type GlobalDialogsProps = {
  isAuthed: boolean
  isNoAccountPage: boolean
}

const GlobalDialogs = ({ isAuthed, isNoAccountPage }: GlobalDialogsProps) => {
  const { loadTimeEntries } = useWorkspaceActions()
  const isMobile = useDeviceStore(state => state.isMobile)
  const navigate = useNavigate()
  const {
    showDialog,
    newTimeEntrySource,
    defaultDate,
    showWelcome,
    setShowDialog,
    setNewTimeEntrySource,
    setShowWelcome,
  } = useWorkspaceUiStore()
  const { codes, minMinutes, minDailyWorkingHours } = useTimeEntriesStore()
  const { selfUser, selectedUser, isDelegationMode } = useUserStore()
  const {
    hasDestination,
    agentEnabled,
    desktopAgentStatus,
    chromeExtensionStatus,
    edgeExtensionStatus,
    supportEnabled,
    showHeartBeatDialog,
    setShowHeartBeatDialog,
  } = useConnectedAppsStore()
  const {
    midnightDialogVisible,
    midnightCountdown,
    handleCancelMidnightClocking,
    handleConfirmMidnightClocking,
  } = useMidnightClocking({
    enabled: isAuthed && !isNoAccountPage,
    loadTimeEntries,
  })

  return (
    <>
      <TimeEntryDeleteConfirmationDialog />
      <GlobalTitleTooltip />
      {showDialog && (
        <NewTimeEntryDialog
          visible={showDialog}
          backgroundRefresh={newTimeEntrySource === 'time-view'}
          selfUser={selfUser}
          isDelegationMode={isDelegationMode}
          selectedUser={selectedUser}
          minMinutes={minMinutes}
          hasDestination={hasDestination}
          codes={codes}
          defaultDate={defaultDate}
          onSuccess={async (result?: {
            updatedEntryIds?: Array<string | number>
            createdEntryId?: string | number | null
          }) => {
            let refreshed = false
            try {
              if (newTimeEntrySource === 'time-view' && result?.updatedEntryIds?.length) {
                await refreshTimeEntryCaches({ queryClient, userId: selectedUser?.id, entryIds: result.updatedEntryIds })
              } else {
                await loadTimeEntries(minMinutes, minDailyWorkingHours, {
                  refreshQueryData: false,
                })
                await Promise.all([
                  queryClient.refetchQueries({
                    queryKey: ['daily-time-entries'],
                    type: 'active',
                  }),
                  queryClient.refetchQueries({
                    queryKey: ['daily-time-slots'],
                    type: 'active',
                  }),
                ])
              }
              refreshed = true
            } catch (error) {
              console.error('Failed to refresh daily time entry data:', error)
            }
            const createdEntryId = result?.createdEntryId
            if (
              refreshed &&
              newTimeEntrySource === 'time-view' &&
              createdEntryId != null
            ) {
              window.requestAnimationFrame(() => {
                useTimeEntriesStore.getState().highlightTimeEntry(createdEntryId)
              })
            }
            if (newTimeEntrySource === 'timeline') {
              navigate(ROUTER_PATH.timeEntries)
            }
          }}
          onClose={() => {
            setShowDialog(false)
            setNewTimeEntrySource(null)
          }}
        />
      )}

      {isAuthed && !isNoAccountPage && (
        <ZendeskWidget enabled={supportEnabled} />
      )}
      {showWelcome && !isMobile && (
        <WelcomeWizard
          chromeExtensionStatus={chromeExtensionStatus}
          desktopAgentStatus={desktopAgentStatus}
          edgeExtensionStatus={edgeExtensionStatus}
          onClose={() => {
            setShowWelcome(false)
          }}
        />
      )}

      <HeartBeatDialog
        visible={showHeartBeatDialog}
        agentEnabled={agentEnabled}
        desktopAgentStatus={desktopAgentStatus}
        chromeExtensionStatus={chromeExtensionStatus}
        edgeExtensionStatus={edgeExtensionStatus}
        onClose={() => setShowHeartBeatDialog(false)}
      />
      <MidnightClockingDialog
        isMobile={isMobile}
        visible={midnightDialogVisible}
        countdown={midnightCountdown}
        onCancel={handleCancelMidnightClocking}
        onConfirm={handleConfirmMidnightClocking}
      />
    </>
  )
}

export default GlobalDialogs
